import viewSitesStyles from './view-sites.styles.css?inline';
import '../../components/state-badge/agwa-state-badge.js';
import { LitElement, html, unsafeCSS } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ApiFactory } from '../../api/agwasuite-api-factory';
import { ApiSiteMaintenance } from '../../api/core/api-site-maintenance';
import { SecurityApi } from '../../api/security/security-api';
import type {
  AdminSiteListItem,
  SecurityDomainDto,
} from '../../api/agwa-api-model';
import {
  collapseOperationTypeCodes,
  getOrganizationTypeLabel,
  mapOrganizationTypeLabels,
} from './shared/organization-type-labels';

import '@shoelace-style/shoelace/dist/components/icon/icon.js';
import '@shoelace-style/shoelace/dist/components/tag/tag.js';
import '@shoelace-style/shoelace/dist/components/button/button.js';
import '../../components/search/agwa-search';
import '../../components/empty-state/agwa-empty-state';
import '../../components/filter/agwa-filter-rail';
import '../../components/filter/agwa-filter-summary';
import { SEARCH_CLAUSE_KEY } from '../../components/filter/agwa-filter-summary';
import type {
  FilterFacet,
  FilterSelection,
} from '../../components/filter/filter-facets';
import {
  isSelectionEmpty,
  selectedValues,
} from '../../components/filter/filter-facets';
import {
  FACET_OPERATING,
  FACET_TYPES,
  OPERATING_ACTIVE,
  OPERATING_INACTIVE,
} from './shared/site-list-facets';
// The repository's standing comparison for enum-like strings arriving from an API response.
// Domain codes are exactly that: the catalogue's casing and a row's casing are both the server's
// to change, so a `===` here would silently stop the type facet matching anything.
import { isSame } from '../../utility/strings/strings-utils';
// The rail's status words come from the same registry the Active column's badge reads, so a
// filter option and the row it narrows to cannot describe one state in two words.
import { resolveState } from '../../utility/state-colours/state-colour-registry';
import { styles as sharedStyles } from '../../styles/shared-styles';
import { WithUserProfileConsumer } from '../../mixins/with-user-profile-consumer';
import { router, resolveRouterPath } from '../../router';

import '../../components/data-table/data-table';
import { terms } from '../../language/terms';
import { preservedCasing } from '../../language/preserved-casing';
import type {
  DataTableColumnDef,
  DataTable,
} from '../../components/data-table/data-table';
import type {
  DataTableDataProvider,
  DataTableDataRequest,
  DataTableDataResponse,
} from '../../components/data-table/data-table-provider';

@customElement('view-sites')
export class ViewSites extends WithUserProfileConsumer(LitElement) {
  @state() private sites: AdminSiteListItem[] = [];
  @state() private loading = false;
  @state() private error: string | null = null;
  @state() private search = '';

  /**
   * Which values are ticked in the filter rail, keyed by facet.
   *
   * One object rather than a field per filter, because the rail, the query sentence and the facet
   * counts all take the whole selection: splitting it would leave several places that can disagree
   * about what is currently applied.
   *
   * A facet with no ticked values narrows nothing, which is what removed the All option the
   * operating-status radio group needed - with independent checkboxes the empty set already says
   * "all", and an explicit All is a third control that can contradict the other two.
   *
   * Holds canonical keys, never the words on screen. Labels are a display concern that changes.
   */
  @state() private filterSelection: FilterSelection = {};

  /**
   * The operation types the rail may offer, from the security catalogue.
   *
   * Filtered to `uiAssignable` exactly as the company list's rail is (`@CO-TYPEFILTER-001`): the
   * flag comes from security-catalog.yml and reaches the client on the DTO, so a type becoming
   * assignable later appears here with no code change, and a type AGWA never assigns is not
   * offered as a filter that matches nothing.
   */
  @state() private availableDomains: SecurityDomainDto[] = [];

  @state() private sitesProvider?: DataTableDataProvider<AdminSiteListItem>;

  static styles = [sharedStyles, unsafeCSS(viewSitesStyles)];

  /** Column definitions for the admin sites table */
  private get siteColumns(): DataTableColumnDef[] {
    return [
      {
        title: 'Name',
        fieldName: 'siteName',
        width: '200px',
        fixed: 'left',
        sortable: true,
      },
      {
        title: preservedCasing(`${terms.imis.value} Ref`),
        fieldName: 'siteImisReference',
        width: '120px',
      },
      {
        title: terms.loginGroup.value,
        fieldName: 'groupName',
        width: '200px',
        sortable: true,
        displayValue: (_table: DataTable, item: AdminSiteListItem) =>
          html`<a
            class="group-link"
            @click=${(e: Event) => {
              e.preventDefault();
              this.navigateToGroup(item.groupId);
            }}
            >${item.groupName}</a
          >`,
      },
      {
        title: terms.userGroupDomain.plural,
        fieldName: 'organizationTypes',
        width: '180px',
        displayValue: (_table: DataTable, item: AdminSiteListItem) =>
          item.organizationTypes && item.organizationTypes.length > 0
            ? html`<div class="org-types">
                ${mapOrganizationTypeLabels(
                  // PSS collapses to PSM anywhere it is shown (architect, 2026-09-17).
                  collapseOperationTypeCodes(item.organizationTypes)
                ).map((type) => html`<sl-tag size="small">${type}</sl-tag>`)}
              </div>`
            : html`<span class="muted">—</span>`,
      },
      {
        title: 'Location',
        fieldName: 'suburb',
        width: '140px',
        displayValue: (_table: DataTable, item: AdminSiteListItem) =>
          item.suburb || item.state
            ? html`<div class="location-cell">
                ${item.suburb ? html`<span>${item.suburb}</span>` : ''}
                ${
                  item.state
                    ? html`<span class="location-state">${item.state}</span>`
                    : ''
                }
              </div>`
            : html`<span class="muted">—</span>`,
      },
      {
        title: 'Active',
        fieldName: 'active',
        width: '90px',
        sortable: true,
        displayValue: (_table: DataTable, item: AdminSiteListItem) =>
          html`<agwa-state-badge
            domain="siteOperating"
            state=${item.active ? 'Active' : 'Inactive'}
          ></agwa-state-badge>`,
      },
      {
        title: 'Actions',
        fieldName: '_actions',
        width: '120px',
        fixed: 'right',
        displayValue: (_table: DataTable, item: AdminSiteListItem) =>
          html`<div class="actions-cell">
            <sl-button
              size="small"
              variant="default"
              href=${resolveRouterPath(`member/${item.groupId}?tab=sites`)}
            >
              View in ${terms.loginGroup.value}
            </sl-button>
          </div>`,
      },
    ];
  }

  connectedCallback() {
    super.connectedCallback();
    void this.fetchData();
  }

  /**
   * The sites and the operation-type catalogue the rail is built from.
   *
   * Both in one pass, in parallel, because the screen is not usable without either: the rows are
   * the list and the catalogue is what the type facet offers, and loading them in sequence would
   * render a rail with an empty facet for as long as the second call took.
   */
  async fetchData() {
    this.loading = true;
    this.error = null;
    this.sites = [];
    this.sitesProvider = undefined;

    try {
      const api = ApiFactory.authenticated(ApiSiteMaintenance);
      const securityApi = ApiFactory.authenticated(SecurityApi);

      const [resp, domainsResp] = await Promise.all([
        api.searchAllSites({
          sliceStart: 0,
          sliceLength: 500,
          requiresCount: true,
        }),
        securityApi.getSecurityDomains(),
      ]);

      if (resp.success && resp.data?.items) {
        this.sites = resp.data.items;
        this.sitesProvider = this.createSitesProvider();
      } else {
        this.error =
          resp.error || `Failed to load ${terms.loginSite.lowerPlural}`;
        return;
      }

      if (domainsResp.success && domainsResp.data?.domains) {
        this.availableDomains = domainsResp.data.domains.filter(
          (d) => d.uiAssignable
        );
      }
    } catch (e: unknown) {
      this.error =
        e instanceof Error
          ? e.message
          : `Error loading ${terms.loginSite.lowerPlural}`;
    } finally {
      this.loading = false;
    }
  }

  /**
   * The sites surviving a given query, over the page's own copy of the data.
   *
   * Parameterised on the selection rather than reading `this.filterSelection` directly, because
   * the facet counts in the rail need this same predicate run with ONE facet ignored. Counted any
   * other way, ticking Active renders Inactive as `0` - no site is in two operating states at
   * once - and a count that reads zero beside a value you can still usefully tick looks like a
   * broken screen.
   *
   * THE DATA PROVIDER CALLS THIS TOO, rather than restating the clauses. When the rows a table
   * shows and the count a sentence claims are filtered by two copies of one predicate, the screen
   * eventually shows an empty state over a table that has rows in it; one function cannot drift
   * from itself.
   */
  private filterSites(
    selection: FilterSelection,
    searchText: string
  ): AdminSiteListItem[] {
    const text = searchText.trim().toLowerCase();
    const operating = selectedValues(selection, FACET_OPERATING);
    const types = selectedValues(selection, FACET_TYPES);

    return this.sites.filter((s) => {
      if (
        text &&
        !s.siteName?.toLowerCase().includes(text) &&
        !s.siteImisReference?.toLowerCase().includes(text) &&
        !s.groupName?.toLowerCase().includes(text) &&
        !s.suburb?.toLowerCase().includes(text)
      ) {
        return false;
      }
      // Nothing ticked and both ticked ask the same question, and the predicate must not
      // distinguish them: "Active or Inactive" is every site, which is what an empty facet
      // already means.
      if (
        operating.length > 0 &&
        !operating.includes(s.active ? OPERATING_ACTIVE : OPERATING_INACTIVE)
      ) {
        return false;
      }
      // A site matches when it carries ANY of the ticked types - values within a facet are
      // alternatives. Compared case-insensitively because both sides are server strings.
      if (
        types.length > 0 &&
        !(s.organizationTypes ?? []).some((code) => isSame(code, ...types))
      ) {
        return false;
      }
      return true;
    });
  }

  /** How many sites the current query shows, for the sentence above the list. */
  private get shownCount(): number {
    return this.filterSites(this.filterSelection, this.search).length;
  }

  /**
   * Whether the query has narrowed the list to nothing, as opposed to there being no sites at all.
   * The two need different empty states: one offers a way to widen the query, the other cannot.
   */
  private get hasNoMatches(): boolean {
    if (this.sites.length === 0) return false;
    return this.shownCount === 0;
  }

  /**
   * The facets the rail renders, with a count beside every value.
   *
   * Each count is taken with its OWN facet's ticks ignored - see `filterSites`. The operating
   * options are declared in a fixed order rather than derived from the rows, so the rail does not
   * reshuffle itself as sites are added or filtered; the type options are derived from the
   * catalogue, because which types exist is AGWA's to change without a deployment.
   */
  private get filterFacets(): FilterFacet[] {
    const countBy = (
      facetKey: string,
      predicate: (s: AdminSiteListItem) => boolean
    ) => {
      const without: FilterSelection = { ...this.filterSelection };
      without[facetKey] = [];
      return this.filterSites(without, this.search).filter(predicate).length;
    };

    return [
      {
        key: FACET_OPERATING,
        title: 'Active',
        phrase: 'that are',
        // Labelled through the state-colour registry, which is what the Active column's badge
        // reads: the filter and the badge beside it cannot then name one state two ways.
        options: [
          {
            value: OPERATING_ACTIVE,
            label: resolveState('siteOperating', 'Active').label,
          },
          {
            value: OPERATING_INACTIVE,
            label: resolveState('siteOperating', 'Inactive').label,
          },
        ].map((o) => ({
          ...o,
          count: countBy(
            FACET_OPERATING,
            (s) =>
              (s.active ? OPERATING_ACTIVE : OPERATING_INACTIVE) === o.value
          ),
        })),
      },
      {
        key: FACET_TYPES,
        title: terms.userGroupDomain.plural,
        phrase: 'operating as',
        // Labelled through the SAME function the row chips use, so the filter and the chips
        // cannot disagree. Taking the catalogue's own `displayName` is what put a retired word
        // in the company list's rail beside rows chipped with the current one.
        options: this.availableDomains.map((d) => ({
          value: d.code,
          label: getOrganizationTypeLabel(d.code),
          count: countBy(FACET_TYPES, (s) =>
            (s.organizationTypes ?? []).some((code) => isSame(code, d.code))
          ),
        })),
      },
    ];
  }

  /** Whether anything at all is narrowing the list, the search box included. */
  private get anyFilterApplied(): boolean {
    return this.search.trim() !== '' || !isSelectionEmpty(this.filterSelection);
  }

  /**
   * Creates a client-side data provider that narrows, sorts and slices the loaded sites.
   *
   * A NEW provider per query rather than a stable one: `agwa-lit-table` re-fetches when its
   * `dataProvider` property changes, which is how this page has always applied a filter, and
   * keeping that mechanism means the rail changes nothing about how the table is refreshed.
   */
  private createSitesProvider(): DataTableDataProvider<AdminSiteListItem> {
    return async (
      request: DataTableDataRequest
    ): Promise<DataTableDataResponse<AdminSiteListItem>> => {
      // The search and every tick, through the one predicate the sentence and the counts read.
      let rows = this.filterSites(this.filterSelection, this.search);

      // Apply sorting if requested
      const [sortDescriptor] = request.sort ?? [];
      if (sortDescriptor) {
        const direction = sortDescriptor.direction === 'desc' ? -1 : 1;
        rows = [...rows].sort((a, b) => {
          const aVal = a[sortDescriptor.field as keyof AdminSiteListItem];
          const bVal = b[sortDescriptor.field as keyof AdminSiteListItem];
          if (typeof aVal === 'boolean' && typeof bVal === 'boolean') {
            return (aVal === bVal ? 0 : aVal ? -1 : 1) * direction;
          }
          if (typeof aVal === 'string' && typeof bVal === 'string') {
            return aVal.localeCompare(bVal) * direction;
          }
          if (typeof aVal === 'number' && typeof bVal === 'number') {
            return (aVal - bVal) * direction;
          }
          return 0;
        });
      }

      // Slice for pagination
      const start = request.sliceStart ?? 0;
      const end = start + request.sliceLength + 1;
      const window = rows.slice(start, end);
      const items = window.slice(0, request.sliceLength);

      return {
        items,
        hasMore: window.length > request.sliceLength,
        totalCount: rows.length,
      };
    };
  }

  /**
   * Narrows the list to the search the box reports, once. The box reports one search twice -
   * `search-input` as the person types and `search-change` when they press Enter or clear - so a
   * value already searched is not searched again, which reloaded the table a second time
   * (#398 C15 d). A clear is reported as `search-clear` and then a change to nothing, so the change
   * finds the box already empty and is the one dropped.
   */
  private handleSearchInput(e: CustomEvent<{ value?: string }>) {
    const value = e.detail.value ?? '';
    if (value === this.search) return;
    this.search = value;
    this.refreshTable();
  }

  private handleSearchClear() {
    this.search = '';
    this.refreshTable();
  }

  /**
   * A box was ticked or unticked in the rail. The rail hands back the whole selection rather than
   * one change, so there is nothing to merge here and no chance of this page's copy drifting from
   * the control's.
   */
  private handleFilterChange(e: CustomEvent<{ selection: FilterSelection }>) {
    this.filterSelection = e.detail.selection;
    this.refreshTable();
  }

  /**
   * Everything narrowing the list is undone at once - the ticks AND the search box.
   *
   * Clearing only the facets would be the more literal reading of a control that sits in the
   * filter rail, and it would be wrong: someone who has searched and filtered and wants the whole
   * list back has one thing in mind, and leaving the search applied hands them a list still
   * narrowed by something they just asked to clear.
   */
  private handleFilterClear() {
    this.filterSelection = {};
    this.search = '';
    this.refreshTable();
  }

  /** One condition was dropped from the query sentence, leaving the rest of the query in place. */
  private handleFilterDrop(e: CustomEvent<{ key: string }>) {
    if (e.detail.key === SEARCH_CLAUSE_KEY) {
      this.search = '';
    } else {
      this.filterSelection = { ...this.filterSelection, [e.detail.key]: [] };
    }
    this.refreshTable();
  }

  /**
   * Re-runs the query. Handing the table a fresh provider is what makes it re-fetch; the provider
   * reads the current search and selection when it is called.
   */
  private refreshTable() {
    this.sitesProvider = this.createSitesProvider();
  }

  private navigateToGroup(groupId: string) {
    void router?.navigate(resolveRouterPath(`member/${groupId}`));
  }

  render() {
    return html`
      <div class="sites-page">
        <div class="page-header">
          <h1>${terms.loginSite.plural}</h1>
        </div>

        <!--
          Search only. The operating-status radio group that used to sit here is a facet in the
          rail below, and the muted "N sites" count is the query sentence's first words - both
          said less than what replaced them and both cost a line above the table.
        -->
        <div class="filter-bar">
          <div class="filters-left">
            <agwa-search
              .value=${this.search}
              placeholder="Search ${terms.loginSite.lowerPlural}..."
              style="--agwa-search-max-width:260px;"
              @search-input=${this.handleSearchInput}
              @search-change=${this.handleSearchInput}
              @search-clear=${this.handleSearchClear}
            ></agwa-search>
          </div>
        </div>

        <!--
          The rail is a SIBLING of the list, not a strip above it. Every value it offers is
          legible without opening anything, and ticking one changes a checkbox that was already
          on screen - so the filters occupy the same space before and after a choice and the rows
          beneath never move.
        -->
        <div class="list-body">
          <agwa-filter-rail
            storage-key="maintenance-sites"
            .facets=${this.filterFacets}
            .selection=${this.filterSelection}
            ?can-clear=${this.anyFilterApplied}
            @filter-change=${this.handleFilterChange}
            @filter-clear=${this.handleFilterClear}
          ></agwa-filter-rail>
          <div class="list-main">
            <!--
              What the ticks MEAN together, in words. The rail can show which boxes are set; only
              this can show that values within a facet are OR'd while facets are AND'ed, which is
              what someone needs when a site they expected is not in the list.
            -->
            <agwa-filter-summary
              .facets=${this.filterFacets}
              .selection=${this.filterSelection}
              search-text=${this.search}
              subject=${terms.loginSite.lowerPlural}
              shown-count=${this.shownCount}
              total-count=${this.sites.length}
              @filter-drop=${this.handleFilterDrop}
            ></agwa-filter-summary>
            ${
              this.loading
                ? html`<agwa-empty-state
                    icon="hourglass-split"
                    title="Loading"
                    message="Fetching ${terms.loginSite.lowerPlural}..."
                  ></agwa-empty-state>`
                : this.error
                  ? html`<agwa-empty-state
                      icon="exclamation-triangle"
                      title="Error"
                      .message=${this.error}
                    ></agwa-empty-state>`
                  : this.sites.length === 0 && !this.search
                    ? html`<agwa-empty-state
                        icon="building"
                        title="No ${terms.loginSite.lowerPlural}"
                        message="No ${terms.loginSite.lowerPlural} have been created in the system yet."
                      ></agwa-empty-state>`
                    : html`
                        ${
                          this.hasNoMatches
                            ? html`<agwa-empty-state
                                icon="search"
                                title="No matches"
                                message="No ${terms.loginSite.lowerPlural} match every condition above. Remove one, or clear them all."
                              >
                                <!--
                                  Clears the ticks AND the search, matching the rail's own
                                  control. Offering only "clear search" here would hand back a
                                  list still narrowed by filters the person had just asked to
                                  be rid of.
                                -->
                                <sl-button
                                  size="small"
                                  @click=${this.handleFilterClear}
                                  >Clear all filters</sl-button
                                >
                              </agwa-empty-state>`
                            : ''
                        }
                        ${
                          this.sitesProvider
                            ? html`<agwa-lit-table
                                fill-height
                                .columns=${this.siteColumns}
                                .dataProvider=${this.sitesProvider}
                                style=${this.hasNoMatches ? 'display: none' : ''}
                              ></agwa-lit-table>`
                            : html``
                        }
                      `
            }
          </div>
        </div>
      </div>
    `;
  }
}
